import { useEffect, useMemo, useRef, useState } from "react";
import SignaturePad from "signature_pad";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";
import { Box, Button, Group, Image, Modal, SegmentedControl, Stack, Text, TextInput } from "@mantine/core";
import { Dropzone } from "@mantine/dropzone";
import "@mantine/dropzone/styles.css";
import { notifications } from "@mantine/notifications";
import { IconSignature, IconUpload } from "@tabler/icons-react";
import { useFirestore, useStorage } from "../../helpers/firebaseContext";
import { useLiveDoc } from "./useLive";
import { CtcStaffProfile } from "../../types";
import { useCtcUser } from "./ctc_helpers";

const SIGNATURE_MAX_BYTES = 2 * 1024 * 1024;
const kuwaitTime = (ts?: { toDate?: () => Date }) => ts?.toDate?.().toLocaleString("en-GB", { timeZone: "Asia/Kuwait", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }).replace(",", "");

/** The drawn signature cropped to its ink (transparent PNG), so it fits the signature line on the PDFs. */
const trimmedSignature = (canvas: HTMLCanvasElement): Promise<File | null> => {
    const ctx = canvas.getContext("2d");
    if (!ctx) return Promise.resolve(null);
    const { width, height } = canvas;
    const pixels = ctx.getImageData(0, 0, width, height).data;
    let top = height, left = width, right = -1, bottom = -1;
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            if (pixels[(y * width + x) * 4 + 3] > 0) {
                if (x < left) left = x;
                if (x > right) right = x;
                if (y < top) top = y;
                if (y > bottom) bottom = y;
            }
        }
    }
    if (right < 0) return Promise.resolve(null);
    const pad = 8;
    left = Math.max(0, left - pad); top = Math.max(0, top - pad);
    right = Math.min(width - 1, right + pad); bottom = Math.min(height - 1, bottom + pad);
    const out = document.createElement("canvas");
    out.width = right - left + 1;
    out.height = bottom - top + 1;
    out.getContext("2d")!.drawImage(canvas, left, top, out.width, out.height, 0, 0, out.width, out.height);
    return new Promise((resolve) => out.toBlob((blob) => resolve(blob ? new File([blob], "signature.png", { type: "image/png" }) : null), "image/png"));
};

/** Signature drawn with mouse, touch or pen (signature_pad). Calls onChange(true) once something is drawn. */
function SignatureDrawBox({ padRef, canvasRef, onChange }: {
    padRef: React.MutableRefObject<SignaturePad | null>; canvasRef: React.RefObject<HTMLCanvasElement | null>; onChange: (drawn: boolean) => void;
}) {
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const pad = new SignaturePad(canvas, { penColor: "#1a237e", minWidth: 0.8, maxWidth: 2.6 }); // transparent background
        padRef.current = pad;
        const changed = () => onChange(!pad.isEmpty());
        pad.addEventListener("endStroke", changed);
        // Sharp lines on high-DPI screens; a resize clears the pad (the size is set before drawing)
        const fit = () => {
            const ratio = Math.max(window.devicePixelRatio || 1, 1);
            canvas.width = canvas.offsetWidth * ratio;
            canvas.height = canvas.offsetHeight * ratio;
            canvas.getContext("2d")!.scale(ratio, ratio);
            pad.clear();
            onChange(false);
        };
        const observer = new ResizeObserver(fit);
        observer.observe(canvas);
        return () => { observer.disconnect(); pad.removeEventListener("endStroke", changed); pad.off(); padRef.current = null; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (
        <Stack gap={4}>
            <Box style={{ border: "1px dashed var(--mantine-color-gray-4)", borderRadius: 8, background: "var(--mantine-color-body)" }}>
                <canvas ref={canvasRef} style={{ width: "100%", height: 180, display: "block", touchAction: "none", cursor: "crosshair" }} />
            </Box>
            <Group justify="space-between">
                <Text size="xs" c="dimmed">Sign above with your mouse, finger or pen</Text>
                <Button size="compact-xs" variant="subtle" color="gray" onClick={() => { padRef.current?.clear(); onChange(false); }}>Clear</Button>
            </Group>
        </Stack>
    );
}

/**
 * "My CTC signature" button (Watch list) for everyone who signs an attachment: display name +
 * signature image printed on the CTC attachments (makers / checkers: 1 and 3; dealers: 2, 4 and 5).
 * Green dot = a signature is saved, red = none yet. No user records exist (Entra only), so the
 * profile doc is created lazily, keyed by email.
 */
export const CtcSignatureButton = () => {
    const db = useFirestore();
    const storage = useStorage();
    const user = useCtcUser();
    const profileRef = useMemo(() => doc(db, "ctc_staff", user.email || "missing"), [db, user.email]);
    const { data: profile } = useLiveDoc<CtcStaffProfile>(user.email ? `ctc_staff/${user.email}` : null);
    const [open, setOpen] = useState(false);
    const [displayName, setDisplayName] = useState("");
    const [file, setFile] = useState<File | null>(null);
    const [saving, setSaving] = useState(false);
    const [currentUrl, setCurrentUrl] = useState<string>();
    const [newUrl, setNewUrl] = useState<string>();
    const [mode, setMode] = useState<"draw" | "upload">("draw");
    const [drawn, setDrawn] = useState(false);
    const padRef = useRef<SignaturePad | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    // Re-fetch the saved signature whenever the profile changes (new upload from any tab / device)
    const signatureVersion = `${profile?.signaturePath ?? ""}:${(profile?.updated as { toMillis?: () => number } | undefined)?.toMillis?.() ?? ""}`;
    useEffect(() => {
        if (!profile?.signaturePath) return setCurrentUrl(undefined);
        let cancelled = false;
        getDownloadURL(storageRef(storage, profile.signaturePath)).then((url) => { if (!cancelled) setCurrentUrl(url); }).catch(() => { if (!cancelled) setCurrentUrl(undefined); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signatureVersion, storage]);
    useEffect(() => {
        if (!file) return setNewUrl(undefined);
        const url = URL.createObjectURL(file);
        setNewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    // Everyone who signs a generated document: makers / checkers (attachments 1, 3), dealers (2, 4, 5), supervisors, admins
    if (!["maker", "checker", "dealer", "supervisor"].some((r) => user.has(r as never)) && !user.isAdmin) return null;

    const hasSignature = !!profile?.signaturePath;
    const openModal = () => { setDisplayName(profile?.displayName ?? ""); setFile(null); setDrawn(false); setMode("draw"); setOpen(true); };
    const newSignature = mode === "draw" ? drawn : !!file;
    const unchanged = !newSignature && displayName.trim() === (profile?.displayName ?? "");

    const save = async () => {
        setSaving(true);
        try {
            const updates: Record<string, unknown> = { email: user.email, displayName: displayName.trim(), updated: serverTimestamp(), updatedBy: user.email };
            const image = mode === "draw" ? (drawn && padRef.current ? await trimmedSignature(canvasRef.current!) : null) : file;
            if (image) {
                const path = `ctc_staff/${user.email}/signature.png`;
                await uploadBytes(storageRef(storage, path), image, { contentType: image.type || "image/png" });
                updates.signaturePath = path;
                updates.signatureUpdatedAt = serverTimestamp(); // when the signature image last changed
            }
            await setDoc(profileRef, updates, { merge: true });
            notifications.show({ color: "green", message: "Signature saved" });
            setOpen(false);
        } catch (e) {
            console.error("ctc profile save failed", e);
            notifications.show({ color: "red", message: (e as Error).message });
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Button variant="default" leftSection={<IconSignature size={16} />} onClick={openModal}
                rightSection={<Box w={8} h={8} style={{ borderRadius: 4, background: `var(--mantine-color-${hasSignature ? "green" : "red"}-6)` }} aria-label={hasSignature ? "Signature saved" : "No signature yet"} />}>
                My CTC signature
            </Button>
            <Modal opened={open} onClose={() => !saving && setOpen(false)} title={<Text fw={600}>My CTC signature</Text>} size="lg">
                <Stack>
                    <Text size="sm" c="dimmed">Printed with your name on the attachments you sign (purchase, transfer and sale confirmations, and your requests).</Text>
                    <TextInput label="Display name" description="Shown under the signature; empty = your email" value={displayName} onChange={(e) => setDisplayName(e.currentTarget.value)} />
                    <div>
                        <Group justify="space-between" mb={6}>
                            <Text size="sm" fw={500}>Signature</Text>
                            <SegmentedControl size="xs" value={mode} onChange={(v) => setMode(v as "draw" | "upload")} data={[{ value: "draw", label: "Draw" }, { value: "upload", label: "Upload" }]} />
                        </Group>
                        {currentUrl && (
                            <Group gap="xs" mb={6}>
                                <Text size="xs" c="dimmed">Current:</Text>
                                <Image src={currentUrl} alt="current signature" h={40} w="auto" fit="contain" />
                                {profile?.signatureUpdatedAt && <Text size="xs" c="dimmed">· updated {kuwaitTime(profile.signatureUpdatedAt)} (Kuwait)</Text>}
                            </Group>
                        )}
                        {mode === "draw" ? <SignatureDrawBox padRef={padRef} canvasRef={canvasRef} onChange={setDrawn} /> : <Dropzone multiple={false} accept={["image/png", "image/jpeg"]} maxSize={SIGNATURE_MAX_BYTES}
                            onDrop={(files) => files[0] && setFile(files[0])}
                            onReject={(rejected) => notifications.show({ color: "red", message: rejected[0]?.errors[0]?.code === "file-too-large" ? "The image must be smaller than 2 MB" : "Choose a PNG or JPEG image" })}>
                            <Group justify="center" gap="lg" mih={140} wrap="nowrap" style={{ pointerEvents: "none" }}>
                                {newUrl ?? currentUrl ? <Image src={newUrl ?? currentUrl} alt="signature" mah={110} maw={280} fit="contain" /> : <IconSignature size={40} color="var(--mantine-color-dimmed)" />}
                                <div>
                                    <Text size="sm" fw={500}>{file ? `New: ${file.name}` : hasSignature ? "Current signature" : "No signature yet"}</Text>
                                    <Text size="xs" c="dimmed">{file ? "Saved when you click Save" : <><IconUpload size={12} /> Click or drop a PNG / JPEG (max 2 MB) to {hasSignature ? "replace it" : "add one"}</>}</Text>
                                </div>
                            </Group>
                        </Dropzone>}
                    </div>
                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
                        <Button onClick={save} loading={saving} disabled={unchanged}>Save</Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    );
};

export default CtcSignatureButton;
